import { useState } from "react";
import { useAlertContext } from "../../context/AlertContext";
import { formatDateRu } from "../../utils/dateFormat";
import { importAssetsFromContacts, searchAssets } from "../../services/assetsApi";
import { AssetHistoryList, holderText, KIND_LABELS, sinceText } from "./assetParts";

/**
 * Поиск по номеру телефона, аккаунту или инвентарному номеру (правка №33):
 * у кого сейчас, кто держал последним (в т.ч. уволенные), вся история передач;
 * плюс люди, у которых это значение есть в профиле или контактах (личные и уволенные тоже).
 * Чат 4, правка №15 (MR 3). [Claude] 2026-11-02
 */
export default function AssetSearchPanel({ onOpenEmployee, canManage = false, onClose }) {
    const { showAlert } = useAlertContext();
    const [q, setQ] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);

    const run = async (event) => {
        event.preventDefault();
        const query = q.trim();
        if (!query) return;
        setLoading(true);
        try {
            setResult(await searchAssets(query));
        } catch (error) {
            showAlert(error.message || "Поиск не удался", "error");
        } finally {
            setLoading(false);
        }
    };

    const fillFromContacts = async () => {
        setImporting(true);
        try {
            const res = await importAssetsFromContacts();
            showAlert(`Из контактов заведено: ${res?.created ?? 0}, пропущено (уже есть или личные): ${res?.skipped ?? 0}`, "success");
        } catch (error) {
            showAlert(error.message || "Не удалось заполнить из контактов", "error");
        } finally {
            setImporting(false);
        }
    };

    const assets = result?.assets || [];
    const people = result?.people || [];

    return <section className="border-b border-slate-100 bg-slate-50/60 px-4 py-3" data-testid="asset-search-panel">
        <form onSubmit={run} className="flex flex-wrap items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Номер телефона, Telegram, почта, логин ERP или инв. номер" className="min-w-[280px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-amber-400" data-testid="asset-search-input" />
            <button type="submit" disabled={loading} className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50" data-testid="asset-search-submit">{loading ? "Ищем…" : "Найти"}</button>
            {canManage ? <button type="button" disabled={importing} onClick={fillFromContacts} className="text-xs text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:opacity-50" data-testid="assets-import-contacts" title="Рабочие и общие номера, Telegram и почта из контактов станут предметами с текущим владельцем. Личные не переносятся. Повторный запуск дублей не создаёт.">{importing ? "Заполняем…" : "Заполнить из контактов"}</button> : null}
            {onClose ? <button type="button" onClick={onClose} className="ml-auto text-xs text-slate-400 hover:text-slate-700">Закрыть</button> : null}
        </form>

        {result ? <div className="mt-3 grid gap-3 lg:grid-cols-2" data-testid="asset-search-results">
            <div>
                <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Техника, номера, аккаунты</h3>
                {!assets.length ? <p className="text-sm text-slate-500">Не найдено</p> : <ul className="space-y-2">
                    {assets.map((a) => <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`asset-search-item-${a.id}`}>
                        <div className="text-sm font-semibold text-slate-900">{a.category}
                            {a.identifier ? <span className="ml-2 font-mono">{a.identifier}</span> : null}
                            {a.inventory_number ? <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">инв. {a.inventory_number}</span> : null}
                            <span className="ml-2 text-xs font-normal text-slate-400">{KIND_LABELS[a.kind] || ""}</span>
                        </div>
                        <div className="mt-1 text-sm">Сейчас: {a.holder?.type === "employee" && a.holder.employee_id
                            ? <button type="button" onClick={() => onOpenEmployee?.(a.holder.employee_id)} className="font-semibold text-amber-700 hover:underline">{holderText(a.holder)}</button>
                            : <b>{holderText(a.holder)}</b>} <span className="text-xs text-slate-400">{sinceText(a)}</span></div>
                        {a.last_employee && a.holder?.type !== "employee" ? <div className="text-sm">Последний сотрудник: <button type="button" onClick={() => onOpenEmployee?.(a.last_employee.employee_id)} className="font-semibold text-amber-700 hover:underline" data-testid={`asset-last-employee-${a.id}`}>{holderText(a.last_employee)}</button></div> : null}
                        <div className="mt-2"><AssetHistoryList items={a.history} /></div>
                    </li>)}
                </ul>}
            </div>
            <div>
                <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Люди с этим значением</h3>
                {!people.length ? <p className="text-sm text-slate-500">Не найдено</p> : <ul className="space-y-2">
                    {people.map((p) => <li key={p.employee_id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`asset-search-person-${p.employee_id}`}>
                        <button type="button" onClick={() => onOpenEmployee?.(p.employee_id)} className="text-sm font-semibold text-slate-900 hover:text-amber-700">{p.full_name}</button>
                        {p.date_fired ? <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">уволен {formatDateRu(p.date_fired)}</span> : null}
                        <div className="text-xs text-slate-500">{[p.position, p.department].filter(Boolean).join(" · ")}</div>
                        <ul className="mt-1 text-xs text-slate-600">{(p.matches || []).map((m, i) => <li key={i}>{m.field}: <span className="font-mono">{m.value}</span></li>)}</ul>
                    </li>)}
                </ul>}
            </div>
        </div> : null}
    </section>;
}
